import { z } from 'zod'
import i18next from 'i18next'
import { Mutator } from './base'
import { DataBase } from '@/lib/kysely'
import type { InsertQuery, UpdateQuery, DeleteQuery } from '@/lib/entity/base'
import { graphql } from '@/gql'
import type { MutationOperationAndVariables } from '@/lib/mutationHelpers'
import { type Reactive } from 'vue'

const TABLE_NAME = 'tenant' as const
const REGEX_IP_ADDR = /^[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\/[0-9]{1,3}$/
const REGEX_DAILY_WORK_TIME_1 = /^[0-1][0-9]:[0-5][0-9]$/ // 00:00 - 19:59
const REGEX_DAILY_WORK_TIME_2 = /^2[0-3]:[0-5][0-9]$/ // 20:00 - 23:59
const DEFAULT_ATTENDANCE_TIME_RANGE_FORMAT = /^[0-9]{2}:[0-5][0-9]-[0-9]{2}:[0-5][0-9]$/

const timeToNumber = (time_str: string) => Number.parseInt(time_str.replace(':', ''))

const isValidTimeRange = (timeRange: string) => {
  const [start_time, end_time] = timeRange.split('-')

  return timeToNumber(end_time) > timeToNumber(start_time)
}

const defaultAttendanceSchema = z
  .object({
    work_time: z
      .string()
      .regex(
        DEFAULT_ATTENDANCE_TIME_RANGE_FORMAT,
        i18next.t('stages.labels.error.tenant.formatError', { ns: 'translation' })
      ),
    break_time: z
      .string()
      .regex(
        DEFAULT_ATTENDANCE_TIME_RANGE_FORMAT,
        i18next.t('stages.labels.error.tenant.formatError', { ns: 'translation' })
      )
      .optional()
  })
  .superRefine((data, ctx) => {
    if (data.break_time === undefined) {
      if (!isValidTimeRange(data.work_time)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18next.t('stages.labels.error.tenant.rangeError', { ns: 'translation' })
        })
      }
    } else {
      if (!isValidTimeRange(data.work_time) || !isValidTimeRange(data.break_time)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18next.t('stages.labels.error.tenant.rangeError', { ns: 'translation' })
        })
      }
    }
  })
  .superRefine((data, ctx) => {
    if (data.break_time === undefined) {
      return
    }

    const [start_work_time, end_work_time] = data.work_time.split('-')
    const [start_break_time, end_break_time] = data.break_time.split('-')

    if (
      !(
        timeToNumber(start_break_time) > timeToNumber(start_work_time) &&
        timeToNumber(end_break_time) < timeToNumber(end_work_time)
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18next.t('stages.labels.error.tenant.breakRangeError', { ns: 'translation' })
      })
    }
  })
  .optional()

const settingsValidationSchema = z
  .object({
    attendance_management: z.boolean().optional(),
    default_period: z.enum(['monthly', 'monthly_short', 'weekly', 'daily']).optional(),
    default_range: z.enum(['short', 'middle', 'long']).optional(),
    password_login: z.boolean(),
    oauth_login: z
      .object({
        provider: z.enum(['okta', 'azure', 'google', 'openam']),
        provider_id: z.string()
      })
      .strict()
      .optional(),
    allowed_ips: z.string().regex(REGEX_IP_ADDR).array().max(10).optional(),
    default_volumed_assign_hourly_cost: z.number().optional(),
    daily_work_time: z
      .string()
      .superRefine((data, ctx) => {
        if (data === '00:00') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18next.t('stages.labels.error.tenant.dailyWorkTime', { ns: 'translation' })
          })
          return
        }
        const isValid = REGEX_DAILY_WORK_TIME_1.test(data) || REGEX_DAILY_WORK_TIME_2.test(data)
        if (!isValid) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18next.t('stages.labels.error.tenant.dailyWorkTime', { ns: 'translation' })
          })
        }
      })
      .optional(),
    user_summary_format: z.enum(['percentage', 'effort']).optional(),
    assign_plan_format: z.enum(['hours_per_day', 'percentage', 'effort']).optional(),
    user_pj_summary_type: z.enum(['average_operating_rate', 'total_effort']).optional(),
    project_field: z
      .object({
        1: z.string().nullable(),
        2: z.string().nullable()
      })
      .optional(),
    mfa_totp: z.boolean().optional().default(false),
    assign_sort_priority: z
      .enum(['role_first', 'display_order_first'])
      .optional()
      .default('role_first'),
    default_attendance: defaultAttendanceSchema,
    user_utilization_thresholds: z
      .array(z.number().int().min(0).max(999))
      .superRefine((arr, ctx) => {
        if (arr.length !== 4) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18next.t('stages.labels.error.tenant.thresholdLength', { ns: 'translation' })
          })
          return
        }
        if (!(arr[0] < arr[1] && arr[1] < arr[2] && arr[2] < arr[3])) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18next.t('stages.labels.error.tenant.thresholdOrder', { ns: 'translation' })
          })
          return
        }
        if (new Set(arr).size !== 4) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18next.t('stages.labels.error.tenant.thresholdDuplicate', {
              ns: 'translation'
            })
          })
        }
      })
      .optional()
  })
  .strict()

const settingsSchema = z
  .string()
  .transform((data) => JSON.parse(data))
  .pipe(settingsValidationSchema)

export const tenantSchema = z.object({
  id: z.string().max(21),
  settings: settingsSchema
})

export type Tenant = z.infer<typeof tenantSchema>
export type Settings = z.infer<typeof settingsValidationSchema>

export class TenantMutator extends Mutator<typeof tenantSchema> {
  static readonly stageableClassName = 'TenantMutator'
  schema = tenantSchema
  table = TABLE_NAME

  static async load(): Promise<Reactive<TenantMutator>> {
    const self = this.createInstance()
    // FIXME: need use watch query
    const base = await DataBase.selectFrom(self.table).select(['id', 'settings']).executeTakeFirst()
    if (!base) throw Error('cannot find Tenant settings')
    const settings =
      typeof base.settings === 'object' ? JSON.stringify(base.settings) : base.settings
    self.base = { ...base, settings }
    self.context = { id: base.id }
    return self
  }

  async updateLocalDB(): Promise<UpdateQuery[]> {
    const rawData = { ...(await this.parsed)! }
    const variables = { settings: JSON.stringify(rawData.settings) }
    return [DataBase.updateTable(this.table).set(variables).compile()]
  }

  async asUpdateMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const id = this.context.id!
    const mutations = []
    const rawData = { ...(await this.parsed)! }
    const variables = rawData.settings
    mutations.push({
      mutation: updateSettingsMutationDocument,
      variables: { id: id, input: variables, commitId: commit_id }
    })
    const currentMfaTotp = JSON.parse(this.base?.settings ?? '{}')['mfa_totp'] === true
    if (variables.mfa_totp !== currentMfaTotp) {
      const mfaStatus = variables.mfa_totp
      mutations.push({
        mutation: updateMfaStatusMutationDocument,
        variables: { status: mfaStatus }
      })
    }
    return mutations
  }

  async insertLocalDB(): Promise<InsertQuery[]> {
    throw Error('can not insert tenant settings')
  }

  async deleteLocalDB(): Promise<DeleteQuery[]> {
    throw Error('can not delete tenant settings')
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async asInsertMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    throw Error('can not insert tenant settings')
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async asDeleteMutation(commitId: string): Promise<MutationOperationAndVariables[]> {
    throw Error('can not delete tenant settings')
  }
}

const updateSettingsMutationDocument = graphql(/* GraphQL */ `
  mutation updateTenantSettingsMutation($id: String!, $input: json!, $commitId: String!) {
    update_coassign_tenant_by_pk(
      pk_columns: { id: $id }
      _set: { settings: $input, commit_id: $commitId }
    ) {
      created_at
    }
  }
`)

const updateMfaStatusMutationDocument = graphql(/* GraphQL */ `
  mutation updateMfaStatusMutation($status: Boolean!) {
    UpdateMfaStatus(status: $status)
  }
`)
